import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Trash2, Plus } from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const EMPTY = { title: "", description: "", price: "", active: true };

export default function SpecialsTab() {
  const [specials, setSpecials] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () =>
    axios.get(`${API}/specials/all`, authHeaders()).then((res) => setSpecials(res.data)).catch(() => toast.error("Could not load specials"));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (special) => {
    setEditing(special);
    setForm({ title: special.title, description: special.description, price: special.price, active: special.active });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/specials/${editing.id}`, form, authHeaders());
        toast.success("Special updated");
      } else {
        await axios.post(`${API}/specials`, form, authHeaders());
        toast.success("Special added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (special) => {
    if (!window.confirm(`Delete special "${special.title}"?`)) return;
    try {
      await axios.delete(`${API}/specials/${special.id}`, authHeaders());
      toast.success("Special deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleActive = async (special) => {
    try {
      await axios.put(
        `${API}/specials/${special.id}`,
        { title: special.title, description: special.description, price: special.price, active: !special.active },
        authHeaders()
      );
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-specials-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-zinc-400">
          {specials.filter((s) => s.active).length} live on the site - only active specials are shown to visitors
        </p>
        <button
          data-testid="specials-add-button"
          onClick={openNew}
          className="inline-flex items-center gap-2 rounded-full bg-disco-yellow text-disco-void text-xs font-bold px-5 py-2.5 transition-all duration-300 hover:shadow-[0_0_20px_rgba(226,255,0,0.35)] active:scale-95"
        >
          <Plus size={14} /> Add Special
        </button>
      </div>

      {specials.length === 0 ? (
        <div className="border border-white/10 py-16 text-center text-zinc-500 text-sm" data-testid="specials-empty-state">
          No specials yet. Add one and it appears on the site while it's active.
        </div>
      ) : (
        <div className="space-y-3">
          {specials.map((special) => (
            <div
              key={special.id}
              data-testid={`special-row-${special.id}`}
              className={`border p-5 flex flex-wrap items-center justify-between gap-4 transition-colors duration-300 ${special.active ? "border-disco-lavender/40 bg-disco-lavender/5" : "border-white/10 bg-disco-surface/40 opacity-60"}`}
            >
              <div className="min-w-0">
                <span className="flex items-center gap-3 flex-wrap">
                  <span className="text-zinc-100 font-medium">{special.title}</span>
                  {special.price && <span className="text-disco-lavender text-sm font-semibold">{special.price}</span>}
                  <span className={`text-[10px] font-bold uppercase tracking-widest ${special.active ? "text-disco-teal" : "text-zinc-600"}`}>
                    {special.active ? "Live" : "Hidden"}
                  </span>
                </span>
                {special.description && (
                  <p className="text-xs text-zinc-500 mt-1 truncate max-w-lg">{special.description}</p>
                )}
              </div>
              <div className="flex items-center gap-1">
                <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer mr-2">
                  <Checkbox
                    data-testid={`special-active-${special.id}`}
                    checked={special.active}
                    onCheckedChange={() => toggleActive(special)}
                    className="border-white/20 data-[state=checked]:bg-disco-lavender data-[state=checked]:border-disco-lavender"
                  />
                  Live
                </label>
                <button
                  data-testid={`special-edit-${special.id}`}
                  onClick={() => openEdit(special)}
                  className="p-2 text-zinc-400 hover:text-disco-teal transition-colors"
                  aria-label={`Edit ${special.title}`}
                >
                  <Pencil size={15} />
                </button>
                <button
                  data-testid={`special-delete-${special.id}`}
                  onClick={() => remove(special)}
                  className="p-2 text-zinc-400 hover:text-red-400 transition-colors"
                  aria-label={`Delete ${special.title}`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="special-dialog" className="bg-disco-surface border-white/10 text-zinc-100 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide text-2xl">
              {editing ? "Edit Special" : "New Special"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs uppercase tracking-widest">Title</Label>
              <Input
                data-testid="special-form-title"
                required
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Disco Fries Upgrade"
                className="bg-white/5 border-white/10 text-zinc-100"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs uppercase tracking-widest">Description</Label>
              <Textarea
                data-testid="special-form-description"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="bg-white/5 border-white/10 text-zinc-100 resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-4 items-end">
              <div className="space-y-2">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest">Price (optional)</Label>
                <Input
                  data-testid="special-form-price"
                  value={form.price}
                  onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                  placeholder="$2"
                  className="bg-white/5 border-white/10 text-zinc-100"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer pb-2">
                <Checkbox
                  data-testid="special-form-active"
                  checked={form.active}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, active: Boolean(v) }))}
                  className="border-white/20 data-[state=checked]:bg-disco-lavender data-[state=checked]:border-disco-lavender"
                />
                Show on site
              </label>
            </div>
            <button
              data-testid="special-save-button"
              type="submit"
              disabled={saving}
              className="w-full rounded-full bg-disco-yellow text-disco-void font-semibold py-3 text-sm transition-all duration-300 hover:shadow-[0_0_20px_rgba(226,255,0,0.35)] active:scale-[0.98] disabled:opacity-50"
            >
              {saving ? "Saving..." : editing ? "Save Changes" : "Add Special"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
